import React, { useMemo, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { ExportCard } from '../ExportCard';
import { exportDeveloperHours } from '../../services';
import { DEFAULT_MONTHS_AHEAD, DEFAULT_MONTHS_BACK } from '../../exportsPage.definitions';
import { addMonths, isValidMonthKey, toMonthKey } from '../../utils';

interface DeveloperHoursExportCardProps {
    onError: (message: string | null) => void;
}

/**
 * Exportação de horas dos desenvolvedores: uma linha por mês (AAAA-MM) com
 * horas disponíveis, alocadas, de Academy e de projetos internos.
 */
export const DeveloperHoursExportCard: React.FC<DeveloperHoursExportCardProps> = ({ onError }) => {
    const defaultRange = useMemo(() => {
        const currentMonth = toMonthKey(new Date());
        return {
            start: addMonths(currentMonth, -DEFAULT_MONTHS_BACK),
            end: addMonths(currentMonth, DEFAULT_MONTHS_AHEAD),
        };
    }, []);

    const [startMonth, setStartMonth] = useState(defaultRange.start);
    const [endMonth, setEndMonth] = useState(defaultRange.end);
    const [isExporting, setIsExporting] = useState(false);
    const [progressLabel, setProgressLabel] = useState<string | null>(null);

    const isRangeValid =
        isValidMonthKey(startMonth) && isValidMonthKey(endMonth) && startMonth <= endMonth;

    const handleExport = async () => {
        if (!isRangeValid) {
            onError('Informe um período válido: o mês inicial deve ser anterior ou igual ao final.');
            return;
        }

        setIsExporting(true);
        setProgressLabel(null);
        onError(null);

        try {
            const developerCount = await exportDeveloperHours(
                { start: startMonth, end: endMonth },
                (loaded, total) => setProgressLabel(`Lendo alocações (${loaded}/${total})...`)
            );

            if (developerCount === 0) {
                onError(
                    'Nenhum colaborador com habilidade principal de desenvolvedor foi encontrado.'
                );
            }
        } catch (error) {
            console.error('Erro ao exportar horas de desenvolvedores:', error);
            onError('Falha ao gerar a exportação de horas. Tente novamente.');
        } finally {
            setIsExporting(false);
            setProgressLabel(null);
        }
    };

    return (
        <ExportCard
            title="Horas de Desenvolvedores"
            description="Uma linha por mês (AAAA-MM) com horas disponíveis, alocadas, de Academy e de projetos internos (OnVue, Raro Digital)."
            icon={ClockIcon}
            isExporting={isExporting}
            disabled={!isRangeValid}
            progressLabel={progressLabel}
            onExport={handleExport}
        >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <label
                        htmlFor="developer-hours-export-start"
                        className="block text-xs font-medium text-gray-500"
                    >
                        Mês inicial
                    </label>
                    <input
                        id="developer-hours-export-start"
                        type="month"
                        value={startMonth}
                        onChange={(event) => setStartMonth(event.target.value)}
                        disabled={isExporting}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                    />
                </div>
                <div>
                    <label
                        htmlFor="developer-hours-export-end"
                        className="block text-xs font-medium text-gray-500"
                    >
                        Mês final
                    </label>
                    <input
                        id="developer-hours-export-end"
                        type="month"
                        value={endMonth}
                        onChange={(event) => setEndMonth(event.target.value)}
                        disabled={isExporting}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                    />
                </div>
            </div>

            {!isRangeValid && (
                <p className="mt-2 text-xs text-red-600">
                    O mês inicial deve ser anterior ou igual ao mês final.
                </p>
            )}
        </ExportCard>
    );
};

export default DeveloperHoursExportCard;
